import { DatasetPipelineStatus, QuestionnaireAnswer } from '@api';
import { useApi, depaginate, mapExistingQuestionnaire } from '@data-access';
import { ErrorHelper } from '@helpers';
import { ChooseModelStepData } from '@sections/data-collection';
import { initialState, questionnaireStore } from '@stores';
import { DataCollectionOptions } from '@types';
import dayjs from 'dayjs';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';

import { DataCollectionShownStep } from '../useDataCollectionNavigation';

import styles from './useDataCollectionModel.module.scss';

interface UseDataCollectionModelParams {
  options: DataCollectionOptions | undefined;
  setShownStep: (step: DataCollectionShownStep) => void;
  setCompleted: (completed: boolean) => void;
}

export const useDataCollectionModel = ({ options, setShownStep, setCompleted }: UseDataCollectionModelParams) => {
  const { api, questionnaireApi, datasetPiplineApi } = useApi();
  const { query } = useRouter();
  const [isPreselecting, setIsPreselecting] = useState(!!query.datasetPipeline || !!query.model); // if there is a dataset pipeline or model in the query, set isPreselecting to true

  const {
    questionnaireData,
    addQuestionnaireData,
    setCurrentStep,
    setMaxStep,
    setQuestionnaireData,
    setQuestionnaireAnswers,
    setSelectedQuestionnaireAnswersOption,
    resetAllModelData,
    resetQuestionnaireAnswersAndSelectedMetrics,
    setLatestAssessment,
    setSelectedRiskMetrics,
  } = questionnaireStore;

  const initialModelSelectionRef = useRef(false);
  const initialDatasetPipelineSelectionRef = useRef(false);

  const selectModel = useCallback(
    async (newInputs: Partial<ChooseModelStepData>, skipReset = false) => {
      if (newInputs.taskType !== questionnaireData?.taskType && !skipReset) {
        resetAllModelData();
      }
      if (newInputs.modelId !== questionnaireData?.modelId) {
        setQuestionnaireData({
          ...initialState,
          ...newInputs,
          modelName: newInputs.modelName,
          useCaseName: newInputs.useCaseName,
          useCaseId: newInputs.useCaseId,
          dataPipeline: questionnaireData?.dataPipeline,
          // `selectModel` is only ever reached via a `?model=` URL, i.e. entering pre-locked to
          // this model (Assess/Reassess model) - unlike picking a dataset, which also sets
          // `modelName` but shouldn't lock the dataset table to that dataset's model.
          isModelLocked: true,
        });
        resetQuestionnaireAnswersAndSelectedMetrics();
      }
      setCurrentStep(1);
      setMaxStep(1);
      setShownStep('datasetInventory');
      setCompleted(false);
    },
    [
      questionnaireData?.dataPipeline,
      questionnaireData?.modelId,
      questionnaireData?.taskType,
      setCurrentStep,
      setMaxStep,
      setQuestionnaireData,
      resetAllModelData,
      resetQuestionnaireAnswersAndSelectedMetrics,
      setShownStep,
      setCompleted,
    ],
  );

  const setModelAndAssessment = useCallback(async () => {
    if (!options) return;
    const model = options.models.find(m => m.id === parseInt(query.model as string, 10));
    if (!model) {
      setIsPreselecting(false);
      return;
    }

    try {
      const fetchedQuestionnaireAnswers = await depaginate(page =>
        questionnaireApi.apiQuestionnaireAnswersList({ page, aiModel: model.id }),
      );

      if (fetchedQuestionnaireAnswers.length === 0) {
        selectModel({
          modelId: model.id,
          useCaseId: model.useCase.id,
          useCaseName: model.useCase.name,
          modelName: model.name,
        });
        addQuestionnaireData({ taskType: model?.taskType });
        window.history.replaceState(null, '', window.location.pathname);
        return;
      }

      let selectedQuestionnaireAnswer: QuestionnaireAnswer | undefined;
      const targetAssessmentId = query.assessment ? parseInt(query.assessment as string, 10) : undefined;
      if (targetAssessmentId) {
        selectedQuestionnaireAnswer = fetchedQuestionnaireAnswers.find(answer =>
          answer.assessments.some(a => a.id === targetAssessmentId),
        );
      } else {
        // TODO: this picks the last answer across all datasets for the model. Once dataset
        // selection is scoped per-dataset, filter by the selected dataset too before popping.
        selectedQuestionnaireAnswer = fetchedQuestionnaireAnswers.pop();
      }
      if (!selectedQuestionnaireAnswer) return;

      const fetchedLatestAssessment = await api.apiAssessmentsRetrieve({
        id: targetAssessmentId ?? selectedQuestionnaireAnswer.assessments[0]?.id,
      });

      await selectModel(
        {
          modelId: model.id,
          useCaseId: model.useCase.id,
          useCaseName: model.useCase.name,
          modelName: model.name,
        },
        true,
      );

      setLatestAssessment(fetchedLatestAssessment);
      setSelectedRiskMetrics(selectedQuestionnaireAnswer.selectedRiskMetrics ?? []);

      setSelectedQuestionnaireAnswersOption({
        label: (
          <div className={styles.selectedValue}>
            <div className={styles.selectedName}>
              <span>
                {targetAssessmentId ?? selectedQuestionnaireAnswer?.assessments[0]?.id
                  ? `Executed Assessment ID - ${targetAssessmentId ?? selectedQuestionnaireAnswer?.assessments[0]?.id}`
                  : 'Draft Assessment'}
              </span>
            </div>
            <div>({dayjs().to(selectedQuestionnaireAnswer.timestamp)})</div>
          </div>
        ),
        value: selectedQuestionnaireAnswer.id,
      });

      const questionnaire = mapExistingQuestionnaire(selectedQuestionnaireAnswer);
      // Prefill the dataset only when reassessing a specific past assessment - the plain
      // "Assess model" flow has no target, so `selectedQuestionnaireAnswer` is just an
      // arbitrary/last answer and its dataset shouldn't be preselected.
      const { datasetPipeline } = selectedQuestionnaireAnswer;
      const isDatasetPipelineUsable =
        datasetPipeline?.status === DatasetPipelineStatus.completed && datasetPipeline?.dataset?.hasPredictions;
      const shouldPrefillDataset = !!targetAssessmentId && !!datasetPipeline;

      addQuestionnaireData({
        ...questionnaire,
        modelName: model.name,
        taskType: model.taskType,
        ...(shouldPrefillDataset && {
          dataPipeline: {
            id: datasetPipeline.id,
            aiModelId: model.id,
            pipelineName: datasetPipeline.taskName,
            dataset: datasetPipeline.dataset,
            perturbedDatasets: datasetPipeline.perturbedDatasets,
          },
        }),
      });
      setQuestionnaireAnswers(fetchedQuestionnaireAnswers.reverse());
      window.history.replaceState(null, '', window.location.pathname);

      // Its dataset is already known and still usable, so skip straight to the Test Section
      // instead of making the user pick it again.
      if (shouldPrefillDataset && isDatasetPipelineUsable) {
        setShownStep('testing');
      }
    } finally {
      setIsPreselecting(false);
    }
  }, [
    api,
    questionnaireApi,
    options,
    query,
    addQuestionnaireData,
    selectModel,
    setQuestionnaireAnswers,
    setSelectedQuestionnaireAnswersOption,
    setSelectedRiskMetrics,
    setLatestAssessment,
    setShownStep,
  ]);

  useEffect(() => {
    if (query.model && options && !initialModelSelectionRef.current) {
      initialModelSelectionRef.current = true;
      setModelAndAssessment();
    }
  }, [options, query.model, setModelAndAssessment]);

  const handleDatasetPipelineInit = useCallback(
    async (datasetPipelineId: number, aiModelIdFromQuery?: number) => {
      try {
        setIsPreselecting(true);
        const fetchedPipeline = await datasetPiplineApi.apiDatasetPipelinesRetrieve({ id: datasetPipelineId });
        const { aiModel } = fetchedPipeline;
        const aiModelId = aiModel?.id ?? aiModelIdFromQuery;

        addQuestionnaireData({
          dataPipeline: {
            id: datasetPipelineId,
            aiModelId,
            pipelineName: fetchedPipeline.taskName,
            dataset: fetchedPipeline.dataset,
            perturbedDatasets: fetchedPipeline.perturbedDatasets,
          },
          ...(aiModel && {
            modelId: aiModel.id,
            modelName: aiModel.name,
            useCaseId: aiModel.useCase?.id,
            useCaseName: aiModel.useCase?.name,
          }),
          // prefer pipeline's taskType, fall back to model's taskType
          ...(fetchedPipeline.taskType ?? aiModel?.taskType
            ? { taskType: fetchedPipeline.taskType ?? aiModel?.taskType }
            : {}),
        });
        window.history.replaceState(null, '', window.location.pathname);
        setShownStep('testing');
      } catch (error) {
        ErrorHelper.handleErrorNotification(error);
      } finally {
        setIsPreselecting(false);
      }
    },
    [addQuestionnaireData, datasetPiplineApi, setShownStep],
  );

  useEffect(() => {
    if (query.datasetPipeline && !initialDatasetPipelineSelectionRef.current) {
      initialDatasetPipelineSelectionRef.current = true;
      const aiModelIdFromQuery = query.aiModelId ? parseInt(query.aiModelId as string, 10) : undefined;
      handleDatasetPipelineInit(parseInt(query.datasetPipeline as string, 10), aiModelIdFromQuery);
    }
  }, [query.datasetPipeline, query.aiModelId, handleDatasetPipelineInit]);

  return { selectModel, isPreselecting };
};
